/**
 * 图片对象持有的成功特效快照，供保存、封面和下载复用
 * 生命周期跟随 EdgelessImageLayer；不依赖全局缓存的容量，不修改历史或协同中的正式配方
 */
import cloneDeep from 'lodash/cloneDeep';
import { cloneOutputCanvas, getLayerOutputDraft, markLayerOutputDraft } from '@/utils/layer-output-draft-kit';
import type { IImageLayerProps, ILayerProps } from '@/version-types';
import { getEffectParams } from '../plugins/image-effect/runtime/effect-params';
import type { IImageEffectCanvasResult } from '../plugins/image-effect/runtime/image-effect-action';
import type { IImageEffectRenderArtifact } from './render-to-canvas';

/** 持有单个图片对象的正式结果和最后一次成功预览，随对象销毁释放 */
export class ImageEffectOutput {
    private artifact?: { params: string; value: IImageEffectRenderArtifact };
    private preview?: { params: string; value: IImageEffectCanvasResult; source?: HTMLCanvasElement };

    /**
     * 保留与渲染输入绑定的正式结果
     * @param layer - 本次渲染的不可变图层快照
     * @param value - 已生成的截图和边界
     * 副作用：替换对象持有的正式特效结果
     */
    retain(layer: ILayerProps, value: IImageEffectRenderArtifact) {
        this.artifact = { params: getEffectParams(layer), value: cloneDeep(value) };
    }

    /**
     * 保留已成功显示的独立特效 Canvas，不在滑杆操作中生成 Base64
     * @param layer - 本次预览图层快照
     * @param value - 独占的 Canvas 和边界
     * @param source - 滤镜或模糊预览使用的底图身份
     * 副作用：替换对象持有的预览结果
     */
    retainPreview(layer: ILayerProps, value: IImageEffectCanvasResult, source?: HTMLCanvasElement) {
        this.preview = { params: getEffectParams(layer), value, source };
    }

    /**
     * 查询与当前输入匹配的正式结果
     * @param layer - 待输出图层
     * @returns 匹配的截图与边界；输入已变化时返回 undefined
     */
    getArtifact(layer: ILayerProps) {
        return this.artifact?.params === getEffectParams(layer) ? cloneDeep(this.artifact.value) : undefined;
    }

    /**
     * 将已完成结果投影到输出副本，不发起新的渲染
     * @param layer - 保存或下载创建的副本
     * @returns 是否找到匹配结果
     * 副作用：只写入副本的派生字段和瞬时 Canvas
     */
    project(layer: ILayerProps) {
        const effect = (layer.data as IImageLayerProps).effect;
        if (!effect) return false;
        const source = getLayerOutputDraft(layer)?.sourceCanvasIdentity;
        if (this.preview?.params === getEffectParams(layer) && this.preview.source === source) {
            const draft = markLayerOutputDraft(layer);
            draft.effectCanvas = cloneOutputCanvas(this.preview.value.canvas);
            draft.effectPosition = cloneDeep(this.preview.value.effectPosition);
            effect.screenshotUrl = '';
            effect.effectPosition = cloneDeep(draft.effectPosition);
            return true;
        }
        const artifact = !source && this.getArtifact(layer);
        if (!artifact) return false;
        effect.screenshotUrl = artifact.screenshotUrl;
        effect.effectPosition = artifact.effectPosition;
        return true;
    }

    /** 退出预览时释放 Canvas，避免取消后的草稿重新参与保存 */
    clearPreview() {
        this.preview = undefined;
    }
}
